import { useState, useRef, useEffect } from "react";
import { CheckIcon } from "@heroicons/react/24/outline";
import { useClickAway } from "react-use";

const Dropdown = ({
  trigger,
  items,
  position = "bottom",
  searchable = false,
  onSearch,
  className = "",
  align = "left",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const containerRef = useRef(null);
  const menuRef = useRef(null);

  useClickAway(containerRef, () => setIsOpen(false));

  useEffect(() => {
    if (isOpen && menuRef.current && selectedIndex >= 0) {
      const menuItems = menuRef.current.querySelectorAll('[role="menuitem"]');
      menuItems[selectedIndex]?.focus();
    }
  }, [selectedIndex, isOpen]);

  const handleKeyDown = (e) => {
    if (!isOpen) return;

    const menuItems = menuRef.current?.querySelectorAll('[role="menuitem"]');
    if (!menuItems) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % menuItems.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex(
          (prev) => (prev - 1 + menuItems.length) % menuItems.length
        );
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0) {
          items[selectedIndex]?.onClick?.();
          setIsOpen(false);
        }
        break;
      case "Escape":
        setIsOpen(false);
        break;
      default: // Added default case
        break;
    }
  };

  const filteredItems = searchable
    ? items.filter((item) =>
        item.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : items;

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
      onKeyDown={handleKeyDown}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between cursor-pointer"
      >
        {typeof trigger === "function" ? trigger(isOpen) : trigger}
      </div>

      {isOpen && (
        <div
          ref={menuRef}
          className={`absolute z-20 mt-2 w-full min-w-[200px] bg-white rounded-lg shadow-xl border border-zinc-100 ${
            position === "top" ? "bottom-full mb-2" : "top-full"
          } ${align === "right" ? "right-0" : "left-0"}`}
        >
          {searchable && (
            <div className="p-2 border-b border-zinc-100">
              <input
                type="text"
                placeholder="Search..."
                className="w-full px-3 py-2 text-sm rounded-md border border-zinc-200 focus:ring-2 focus:ring-zinc-900/20"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  onSearch?.(e.target.value);
                }}
                autoFocus
              />
            </div>
          )}

          <div
            role="menu"
            className="py-2 text-sm text-zinc-700 max-h-60 overflow-y-auto"
          >
            {filteredItems.map((item, index) => (
              <div key={item.label}>
                {item.type === "divider" ? (
                  <hr className="my-2 border-zinc-100" />
                ) : (
                  <button
                    role="menuitem"
                    tabIndex={-1}
                    onClick={() => {
                      item.onClick?.();
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center px-4 py-2.5 hover:bg-zinc-50 ${
                      selectedIndex === index ? "bg-zinc-50" : ""
                    } ${
                      item.disabled
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    }`}
                  >
                    {item.icon && (
                      <span className="mr-3 text-zinc-400">
                        <item.icon className="w-5 h-5" />
                      </span>
                    )}
                    <span className="text-left flex-1">{item.label}</span>
                    {item.selected && (
                      <CheckIcon className="w-5 h-5 ml-2 text-zinc-900" />
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
